import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * Chart of Accounts (CoA) mapping
 */
export type TChartOfAccounts = {
    id: string;
    companyId: string;
    number: string;
    shortName: string;
    longName: string | null;
    rawAccountCode: string | null;
    accountCategory: string | null;
    category: string | null;
    entryType: string;
    isProhibited: boolean;
    isActive: boolean;
};

/**
 * Account mapping (local to BRE)
 */
export type TAccountMapping = {
    id: string;
    dealId: string;
    sourceAccountNumber: string;
    sourceAccountName: string | null;
    chartOfAccountsId: string;
    chartOfAccountsNumber: string;
    chartOfAccountsName: string;
    isActive: boolean;
};

/**
 * Property code
 */
export type TPropertyCode = {
    id: string;
    companyId: string;
    code: string;
    description: string | null;
    endOfFinancialYear: number;
    isActive: boolean;
};

/**
 * Property code mapping
 */
export type TPropertyCodeMapping = {
    id: string;
    dealId: string;
    sourcePropertyCode: string;
    propertyCodeId: string;
    propertyCode: string;
    isActive: boolean;
};

/**
 * Segment value
 */
export type TSegmentValue = {
    id: string;
    companyId: string;
    segmentNumber: number;
    code: string;
    description: string | null;
    yardiSegmentValue: string | null;
    isActive: boolean;
};

/**
 * Segment custom value (per-account)
 */
export type TSegmentCustomValue = {
    id: string;
    companyId: string;
    accountCode: string;
    segmentType: number;
    code: string;
    description: string;
    propertyCode: string | null;
    isActive: boolean;
};

/**
 * Segmentation mapping (per-account rules)
 */
export type TSegmentationMapping = {
    id: string;
    companyId: string;
    accountCode: string;
    isMandatorySegment2: boolean;
    isMandatorySegment4: boolean;
    isMandatorySegment5: boolean;
    isMandatorySegment6: boolean;
    isMandatorySegment7: boolean;
    hasCustomSegment2: boolean;
    hasCustomSegment4: boolean;
    hasCustomSegment5: boolean;
    hasCustomSegment6: boolean;
    hasCustomSegment7: boolean;
    isActive: boolean;
};

/**
 * Segment mapping
 */
export type TSegmentMapping = {
    id: string;
    dealId: string;
    chartOfAccountsId: string;
    segmentNumber: number;
    sourceValue: string | null;
    segmentValueId: string;
    segmentValue: string;
    isMandatory: boolean;
    isActive: boolean;
};

/**
 * Book (global company-level)
 */
export type TBook = {
    id: string;
    companyId: string;
    number: number;
    name: string;
    isBudgetBook: boolean;
    isActive: boolean;
};

/**
 * Mappings slice state
 */
export type TMappingsState = {
    // Reference data
    booksState: TAsyncState<TBook[]>;
    chartOfAccountsState: TAsyncState<TChartOfAccounts[]>;
    propertyCodesState: TAsyncState<TPropertyCode[]>;
    segmentValuesState: TAsyncState<TSegmentValue[]>;
    segmentCustomValuesState: TAsyncState<TSegmentCustomValue[]>;
    segmentationMappingsState: TAsyncState<TSegmentationMapping[]>;

    // Deal-specific mappings
    accountMappingsState: TAsyncState<TAccountMapping[]>;
    propertyCodeMappingsState: TAsyncState<TPropertyCodeMapping[]>;
    segmentMappingsState: TAsyncState<TSegmentMapping[]>;

    // Selected segment number for filtering
    selectedSegmentNumber: number | null;
};

/**
 * Mappings slice actions
 */
export type TMappingsActions = {
    // Books
    setBooksLoading: () => void;
    setBooks: (data: TBook[]) => void;
    setBooksError: (error: Error) => void;

    // Chart of Accounts
    setChartOfAccountsLoading: () => void;
    setChartOfAccounts: (data: TChartOfAccounts[]) => void;
    setChartOfAccountsError: (error: Error) => void;

    // Property Codes
    setPropertyCodesLoading: () => void;
    setPropertyCodes: (data: TPropertyCode[]) => void;
    setPropertyCodesError: (error: Error) => void;

    // Segment Values
    setSegmentValuesLoading: () => void;
    setSegmentValues: (data: TSegmentValue[]) => void;
    setSegmentValuesError: (error: Error) => void;
    patchSegmentValue: (id: string, partial: Partial<TSegmentValue>) => void;
    removeSegmentValue: (id: string) => void;

    // Segment Custom Values
    setSegmentCustomValuesLoading: () => void;
    setSegmentCustomValues: (data: TSegmentCustomValue[]) => void;
    setSegmentCustomValuesError: (error: Error) => void;
    patchSegmentCustomValue: (id: string, partial: Partial<TSegmentCustomValue>) => void;
    removeSegmentCustomValue: (id: string) => void;

    // Segmentation Mappings
    setSegmentationMappingsLoading: () => void;
    setSegmentationMappings: (data: TSegmentationMapping[]) => void;
    setSegmentationMappingsError: (error: Error) => void;
    patchSegmentationMapping: (id: string, partial: Partial<TSegmentationMapping>) => void;
    removeSegmentationMapping: (id: string) => void;

    // Account Mappings
    setAccountMappingsLoading: () => void;
    setAccountMappings: (data: TAccountMapping[]) => void;
    setAccountMappingsError: (error: Error) => void;

    // Property Code Mappings
    setPropertyCodeMappingsLoading: () => void;
    setPropertyCodeMappings: (data: TPropertyCodeMapping[]) => void;
    setPropertyCodeMappingsError: (error: Error) => void;

    // Segment Mappings
    setSegmentMappingsLoading: () => void;
    setSegmentMappings: (data: TSegmentMapping[]) => void;
    setSegmentMappingsError: (error: Error) => void;

    // Selection
    setSelectedSegmentNumber: (segmentNumber: number | null) => void;

    // Reset
    resetMappings: () => void;
};

/**
 * Complete mappings slice type
 */
export type TSliceMappings = TMappingsState & TMappingsActions;

const initialState: TMappingsState = {
    booksState: initialAsyncState(),
    chartOfAccountsState: initialAsyncState(),
    propertyCodesState: initialAsyncState(),
    segmentValuesState: initialAsyncState(),
    segmentCustomValuesState: initialAsyncState(),
    segmentationMappingsState: initialAsyncState(),
    accountMappingsState: initialAsyncState(),
    propertyCodeMappingsState: initialAsyncState(),
    segmentMappingsState: initialAsyncState(),
    selectedSegmentNumber: null,
};

/**
 * Creates the mappings slice for the store.
 * Handles reference data and mapping configurations.
 */
export const createSliceMappings = (): TStateCreator<TSliceMappings> => {
    return (set) => ({
        ...initialState,

        // Books
        setBooksLoading: () => {
            set((state) => ({
                ...state,
                booksState: pendingAsyncState(state.booksState.data),
            }));
        },

        setBooks: (data: TBook[]) => {
            set({ booksState: fulfilledAsyncState(data) });
        },

        setBooksError: (error: Error) => {
            set({ booksState: rejectedAsyncState(error) });
        },

        // Chart of Accounts
        setChartOfAccountsLoading: () => {
            set((state) => ({
                ...state,
                chartOfAccountsState: pendingAsyncState(state.chartOfAccountsState.data),
            }));
        },

        setChartOfAccounts: (data: TChartOfAccounts[]) => {
            set({ chartOfAccountsState: fulfilledAsyncState(data) });
        },

        setChartOfAccountsError: (error: Error) => {
            set({ chartOfAccountsState: rejectedAsyncState(error) });
        },

        // Property Codes
        setPropertyCodesLoading: () => {
            set((state) => ({
                ...state,
                propertyCodesState: pendingAsyncState(state.propertyCodesState.data),
            }));
        },

        setPropertyCodes: (data: TPropertyCode[]) => {
            set({ propertyCodesState: fulfilledAsyncState(data) });
        },

        setPropertyCodesError: (error: Error) => {
            set({ propertyCodesState: rejectedAsyncState(error) });
        },

        // Segment Values
        setSegmentValuesLoading: () => {
            set((state) => ({
                ...state,
                segmentValuesState: pendingAsyncState(state.segmentValuesState.data),
            }));
        },

        setSegmentValues: (data: TSegmentValue[]) => {
            set({ segmentValuesState: fulfilledAsyncState(data) });
        },

        setSegmentValuesError: (error: Error) => {
            set({ segmentValuesState: rejectedAsyncState(error) });
        },

        patchSegmentValue: (id: string, partial: Partial<TSegmentValue>) => {
            set((state) => ({
                ...state,
                segmentValuesState: fulfilledAsyncState(
                    (state.segmentValuesState.data ?? []).map((item) =>
                        item.id === id ? { ...item, ...partial } : item,
                    ),
                ),
            }));
        },

        removeSegmentValue: (id: string) => {
            set((state) => ({
                ...state,
                segmentValuesState: fulfilledAsyncState(
                    (state.segmentValuesState.data ?? []).filter((item) => item.id !== id),
                ),
            }));
        },

        // Segment Custom Values
        setSegmentCustomValuesLoading: () => {
            set((state) => ({
                ...state,
                segmentCustomValuesState: pendingAsyncState(state.segmentCustomValuesState.data),
            }));
        },
        setSegmentCustomValues: (data: TSegmentCustomValue[]) => {
            set({ segmentCustomValuesState: fulfilledAsyncState(data) });
        },
        setSegmentCustomValuesError: (error: Error) => {
            set({ segmentCustomValuesState: rejectedAsyncState(error) });
        },

        patchSegmentCustomValue: (id: string, partial: Partial<TSegmentCustomValue>) => {
            set((state) => ({
                ...state,
                segmentCustomValuesState: fulfilledAsyncState(
                    (state.segmentCustomValuesState.data ?? []).map((item) =>
                        item.id === id ? { ...item, ...partial } : item,
                    ),
                ),
            }));
        },

        removeSegmentCustomValue: (id: string) => {
            set((state) => ({
                ...state,
                segmentCustomValuesState: fulfilledAsyncState(
                    (state.segmentCustomValuesState.data ?? []).filter((item) => item.id !== id),
                ),
            }));
        },

        // Segmentation Mappings
        setSegmentationMappingsLoading: () => {
            set((state) => ({
                ...state,
                segmentationMappingsState: pendingAsyncState(state.segmentationMappingsState.data),
            }));
        },
        setSegmentationMappings: (data: TSegmentationMapping[]) => {
            set({ segmentationMappingsState: fulfilledAsyncState(data) });
        },
        setSegmentationMappingsError: (error: Error) => {
            set({ segmentationMappingsState: rejectedAsyncState(error) });
        },

        patchSegmentationMapping: (id: string, partial: Partial<TSegmentationMapping>) => {
            set((state) => ({
                ...state,
                segmentationMappingsState: fulfilledAsyncState(
                    (state.segmentationMappingsState.data ?? []).map((item) =>
                        item.id === id ? { ...item, ...partial } : item,
                    ),
                ),
            }));
        },

        removeSegmentationMapping: (id: string) => {
            set((state) => ({
                ...state,
                segmentationMappingsState: fulfilledAsyncState(
                    (state.segmentationMappingsState.data ?? []).filter((item) => item.id !== id),
                ),
            }));
        },

        // Account Mappings
        setAccountMappingsLoading: () => {
            set((state) => ({
                ...state,
                accountMappingsState: pendingAsyncState(state.accountMappingsState.data),
            }));
        },

        setAccountMappings: (data: TAccountMapping[]) => {
            set({ accountMappingsState: fulfilledAsyncState(data) });
        },

        setAccountMappingsError: (error: Error) => {
            set({ accountMappingsState: rejectedAsyncState(error) });
        },

        // Property Code Mappings
        setPropertyCodeMappingsLoading: () => {
            set((state) => ({
                ...state,
                propertyCodeMappingsState: pendingAsyncState(state.propertyCodeMappingsState.data),
            }));
        },

        setPropertyCodeMappings: (data: TPropertyCodeMapping[]) => {
            set({ propertyCodeMappingsState: fulfilledAsyncState(data) });
        },

        setPropertyCodeMappingsError: (error: Error) => {
            set({ propertyCodeMappingsState: rejectedAsyncState(error) });
        },

        // Segment Mappings
        setSegmentMappingsLoading: () => {
            set((state) => ({
                ...state,
                segmentMappingsState: pendingAsyncState(state.segmentMappingsState.data),
            }));
        },

        setSegmentMappings: (data: TSegmentMapping[]) => {
            set({ segmentMappingsState: fulfilledAsyncState(data) });
        },

        setSegmentMappingsError: (error: Error) => {
            set({ segmentMappingsState: rejectedAsyncState(error) });
        },

        // Selection
        setSelectedSegmentNumber: (segmentNumber: number | null) => {
            set({ selectedSegmentNumber: segmentNumber });
        },

        // Reset
        resetMappings: () => {
            set(initialState);
        },
    });
};
